import { useState, useEffect, useCallback, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Layout, Skeleton } from 'antd'
import Shroud from '@OpsQuality/components/Shroud'
import { SyntheticEvent } from '@OpsQuality/JobRequestDetails/components/types'
import styles from '@OpsQuality/styles.css'
import { FORBIDDEN_MESSAGE, TOP_NAV_CONTENTS } from '@OpsWb/constants'
import { getJobRequestDetails } from '@OpsQuality/JobRequestDetails/query'

import Box from '~/core/Box'
import { NavigationContext } from '~/core/NavigationConfig'
import Text from '~/core/Text'
import ErrorLabel from '~/components/ErrorLabel'
import SecurePlusIcon from '~/components/SecurePlusIcon'
import { OPERATIONS, OPS_QUALITY_PATH } from '~/redux-modules/preferences'

import { adminJobRequestQuery as AdminJobRequestQueryResultType } from './__generated__/adminJobRequestQuery'
import TopNav from './components/TopNav'
import DetailsContainer from './components/DetailsContainer'

type Params = {
    jobRequestId: string
    tab: string
}

const mobileWidth = 576

const JobRequestDetailsPage = () => {
    const params = useParams<Params>()
    const navigate = useNavigate()
    const [isMobile, setIsMobile] = useState(window.innerWidth < mobileWidth)

    const { setNavigationConfig } = useContext(NavigationContext)

    useEffect(() => {
        setNavigationConfig({
            navTitle: TOP_NAV_CONTENTS.QUALITY,
            sidebar: OPERATIONS,
            showOpsNav: true,
        })
    }, [])

    const updateDimension = useCallback(() => {
        if (window.innerWidth < mobileWidth) {
            return setIsMobile(true)
        }
        setIsMobile(false)
    }, [])

    useEffect(() => {
        window.addEventListener('resize', updateDimension)

        return () => {
            window.removeEventListener('resize', updateDimension)
        }
    }, [updateDimension])

    const { data, loading, error } = useQuery<AdminJobRequestQueryResultType>(
        getJobRequestDetails,
        {
            variables: {
                id: Number(params.jobRequestId),
            },
        },
    )
    const jobRequest = data?.jobRequest
    const goBack = (event: SyntheticEvent) => {
        event.preventDefault()
        navigate(OPS_QUALITY_PATH)
    }

    useEffect(() => {
        if (error?.graphQLErrors[0].message === FORBIDDEN_MESSAGE) {
            navigate(`/admin/app/403`)
        }
    }, [navigate, error])

    if (loading) {
        return (
            <Box padding="16px" margin="16px 24px" backgroundColor="white">
                <Skeleton active loading />
            </Box>
        )
    } else if (error) {
        return <ErrorLabel>Some error occurred</ErrorLabel>
    } else {
        return (
            <Layout className={styles.jobRequestDetailsLayoutList}>
                <Shroud
                    bgColor="success"
                    textColor="white"
                    visible={jobRequest?.project?.isSecurePlus}
                    overlay
                    title={
                        <span>
                            <SecurePlusIcon />
                            <Text fontSize="12px">Secure+</Text>
                        </span>
                    }
                >
                    <Layout.Content className={styles.layoutListContent}>
                        <TopNav goBack={goBack} jobRequest={jobRequest} />
                        <DetailsContainer
                            jobRequest={jobRequest}
                            isMobile={isMobile}
                        />
                    </Layout.Content>
                </Shroud>
            </Layout>
        )
    }
}

export default JobRequestDetailsPage
